import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { truckDeliveryDate, truckLoadingDate } from '../../../utils/truckDates';
import { getTransportStatusDisplay } from '../../transports/transportRouteViews';
import type { OrderStepContext } from './orderStepContext';
import { activeOrderTransports, activeSupplierDraft } from './orderProcessFacts';

export interface OrderScheduleItem {
  key: string;
  /** ISO date or null while nobody has set it. */
  date: string | null;
  /**
   * A truck step is done by the truck's status, not by its date: a truck
   * planned for today has not left yet (the goods may still be missing).
   * Absent = done once the date has passed.
   */
  done?: boolean;
}

/** The truck is on its way or delivered (not just planned). */
const truckLeft = (truck: { status?: string | null }) => {
  const display = getTransportStatusDisplay(String(truck.status || ''));
  return display === 'inProgress' || display === 'completed';
};

const sortedDates = (dates: Array<string | null | undefined>) => dates.filter((date): date is string => Boolean(date)).sort();
const earliest = (dates: Array<string | null | undefined>): string | null => sortedDates(dates)[0] ?? null;
const latest = (dates: Array<string | null | undefined>): string | null => sortedDates(dates).at(-1) ?? null;

/**
 * The dates the order already carries, in the order they happen: ordered,
 * factory ready, loaded at the factory, sent to the customer, delivered.
 * The ZD draft's dates (unless it was rejected or the ZD cancelled) take precedence over the ZK's own.
 * A truck's dates come from utils/truckDates (loading / planned delivery as the
 * transport form saves them, legacy columns as fallback); the truck that takes
 * the goods to the customer is the best word about the delivery, so its date
 * wins over the ZD draft and the ZK field (the last truck = everything there);
 * when every such truck is delivered, the order shows „Dostarczono” with the
 * last actual delivery. Truck steps are ticked by truck status, not by date.
 * Nothing is invented — a date nobody has set reads „do ustalenia”.
 */
export function getOrderScheduleItems(ctx: OrderStepContext): OrderScheduleItem[] {
  const { order } = ctx;
  const trucks = activeOrderTransports(ctx.transports);
  const inbound = trucks.filter((truck) => truck.direction === 'INBOUND');
  const outbound = trucks.filter((truck) => truck.direction === 'OUTBOUND');
  const factoryDirect = order.fulfillmentSource === 'FACTORY_DIRECT';
  // The ZD draft is Logistyk's word about the supplier, so its dates win over what was typed on the ZK.
  const draft = activeSupplierDraft(ctx.stockStatus);
  const draftActive = Boolean(draft);
  const draftReady = draftActive ? draft?.estimatedCompletionDate || null : null;
  // Straight from the factory the ZD delivery date is the delivery to the customer.
  const draftDelivery = draftActive && factoryDirect ? draft?.expectedDelivery || null : null;
  const items: OrderScheduleItem[] = [
    { key: 'ordered', date: order.orderDate || null },
    { key: 'factoryReady', date: draftReady || order.estimatedCompletionDate || null },
    {
      key: 'factoryPickup',
      date: earliest([
        ...inbound.map(truckLoadingDate),
        // The trucks of the draft ZD (Logistyk's own planning) count too, also when only the ZD is on them.
        ...(draftActive ? (draft?.transports ?? []).map(truckLoadingDate) : []),
      ]),
      done: [...inbound, ...(draftActive ? draft?.transports ?? [] : [])].some(truckLeft),
    },
  ];
  if (!factoryDirect) items.push({ key: 'shipment', date: earliest(outbound.map(truckLoadingDate)), done: outbound.some(truckLeft) });
  // Straight from the factory the inbound truck („Fabryka → klient”) is the one that reaches the customer.
  const customerTrucks = factoryDirect ? inbound : outbound;
  const trucksDelivered = customerTrucks.length > 0 && customerTrucks.every((truck) => Boolean(truck.actualDelivery));
  const truckDelivery = latest(customerTrucks.map(truckDeliveryDate));
  if (order.actualDelivery) items.push({ key: 'delivered', date: order.actualDelivery });
  else if (trucksDelivered) items.push({ key: 'delivered', date: truckDelivery });
  else items.push({ key: 'expectedDelivery', date: truckDelivery || draftDelivery || order.expectedDelivery || null, done: false });
  return items;
}

/** „Harmonogram i terminy” (mockup): a vertical timeline, the first date still ahead is the active one. */
export default function OrderSchedule({ ctx }: { ctx: OrderStepContext }) {
  const { t } = useTranslation(['orders']);
  const items = getOrderScheduleItems(ctx);
  const today = dayjs().format('YYYY-MM-DD');
  const isDone = (item: OrderScheduleItem) => item.done ?? (Boolean(item.date) && String(item.date).slice(0, 10) <= today);
  const activeKey = items.find((item) => !isDone(item))?.key;
  return (
    <ol className="zk-schedule" data-testid="zk-schedule">
      {items.map((item) => {
        const state = isDone(item) ? 'done' : item.key === activeKey ? 'active' : 'todo';
        return (
          <li key={item.key} className={`zk-schedule__item zk-schedule__item--${state}`} data-testid={`zk-schedule-${item.key}`} data-state={state}>
            <span className="zk-schedule__dot" aria-hidden="true">{state === 'done' ? <CheckOutlined /> : null}</span>
            <span className="zk-schedule__text">
              <b>{t(`orders:process.schedule.${item.key}`)}</b>
              <small>{t(`orders:process.schedule.${item.key}Hint`)}</small>
            </span>
            <span className="zk-schedule__date">{item.date ? formatDateEuropean(item.date) : t('orders:process.schedule.noDate')}</span>
          </li>
        );
      })}
    </ol>
  );
}
